import { useState } from 'react';
import { Plus, Trash2, Pencil } from 'lucide-react';
import { useCannedResponses, CannedResponse } from '../../hooks/useCannedResponses';
import { Card, Modal, EmptyState, SkeletonCard } from '../../components/ui';
import Button from '../../components/Button';

function Editor({ initial, onClose, onSave }: {
  initial?: CannedResponse;
  onClose: () => void;
  onSave: (data: { title: string; body: string; category?: string }) => Promise<boolean>;
}) {
  const [title, setTitle] = useState(initial?.title ?? '');
  const [category, setCategory] = useState(initial?.category ?? '');
  const [body, setBody] = useState(initial?.body ?? '');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    const ok = await onSave({ title, body, category: category || undefined });
    setSaving(false);
    if (ok) onClose();
  };

  return (
    <Modal open onClose={onClose} size="lg" title={initial ? 'Edit template' : 'New template'}
      footer={<><Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button onClick={save} disabled={saving || !title || !body}>Save</Button></>}>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Title</label>
            <input value={title} onChange={e => setTitle(e.target.value)}
              className="w-full text-sm rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand/30" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Category (optional)</label>
            <input value={category} onChange={e => setCategory(e.target.value)} placeholder="e.g. Access"
              className="w-full text-sm rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand/30" />
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Message</label>
          <textarea value={body} onChange={e => setBody(e.target.value)} rows={6}
            className="w-full text-sm rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand/30" />
        </div>
      </div>
    </Modal>
  );
}

export default function CannedResponses() {
  const { responses, loading, create, update, remove } = useCannedResponses(true);
  const [editing, setEditing] = useState<CannedResponse | null>(null);
  const [creating, setCreating] = useState(false);

  return (
    <div className="p-5 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-navy">Canned Responses</h1>
          <p className="text-sm text-gray-500">Reusable reply templates agents can insert.</p>
        </div>
        <Button onClick={() => setCreating(true)}><Plus size={15} /> New template</Button>
      </div>

      {loading ? (
        <div className="grid gap-3 sm:grid-cols-2">{[0, 1].map(i => <SkeletonCard key={i} />)}</div>
      ) : responses.length === 0 ? (
        <Card><EmptyState title="No templates yet" description="Create reusable replies to speed up agent responses." /></Card>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {responses.map(r => (
            <Card key={r.id} title={r.title} actions={<>
              <button onClick={() => setEditing(r)} className="text-gray-300 hover:text-brand"><Pencil size={14} /></button>
              <button onClick={() => remove(r.id)} className="text-gray-300 hover:text-red-500"><Trash2 size={14} /></button>
            </>}>
              {r.category && <span className="inline-block text-xs bg-gray-100 text-gray-500 rounded-full px-2 py-0.5 mb-2">{r.category}</span>}
              <p className="text-sm text-gray-600 whitespace-pre-wrap line-clamp-4">{r.body}</p>
            </Card>
          ))}
        </div>
      )}

      {creating && <Editor onClose={() => setCreating(false)} onSave={create} />}
      {editing && <Editor initial={editing} onClose={() => setEditing(null)}
        onSave={d => update(editing.id, d)} />}
    </div>
  );
}
